// Story 2.3, 2.4, 2.6: Petty Cash Entry form modal (create + edit)
import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, Upload, Camera, ReceiptText, FileText, ExternalLink, Trash2 } from 'lucide-react';
import {
  useCreatePettyCash,
  useUpdatePettyCash,
  usePettyCashEntries,
  useCategories,
  useDeleteReceipt,
  claimsKeys,
  type CreatePettyCashInput,
  type Receipt,
} from '../../hooks/useClaims';
import imageCompression from 'browser-image-compression';
import { ApiError } from '../../lib/api';
import { AnimatedModal } from './AnimatedModal';

interface PettyCashFormModalProps {
  claimId: string;
  entryId?: string;
  readOnly?: boolean;
  onClose: () => void;
}

function today(): string {
  return new Date().toISOString().split('T')[0]!;
}

function ReceiptPreviewModal({ receipt, onClose }: { receipt: { id: string; originalFilename?: string; mimeType?: string }; onClose: () => void }) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const receiptUrl = `/api/v1/claims/receipts/${receipt.id}`;
  const isPdf = receipt.mimeType === 'application/pdf' || receipt.originalFilename?.toLowerCase().endsWith('.pdf');

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4" role="dialog" aria-modal="true" aria-label="Receipt preview">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        aria-label="Close receipt preview"
      />
      <div className="relative flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            {isPdf ? <FileText className="h-4 w-4 text-primary" /> : <ReceiptText className="h-4 w-4 text-primary" />}
            <h3 className="font-semibold text-sm truncate max-w-md">
              {receipt.originalFilename || 'Receipt Preview'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-text-2 hover:text-text px-2 py-1 rounded hover:bg-surface-2 transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span>Open in new tab</span>
            </a>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close receipt preview"
              className="flex h-8 w-8 items-center justify-center rounded-md text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-black/5 p-3">
          {isPdf ? (
            <iframe
              src={receiptUrl}
              title={receipt.originalFilename || 'PDF Receipt'}
              className="h-[75vh] w-full rounded border-0 bg-white"
            />
          ) : (
            <img
              src={receiptUrl}
              alt={receipt.originalFilename || 'Receipt'}
              className="max-h-[calc(95vh-65px)] max-w-full object-contain rounded"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function PettyCashFormModal({ claimId, entryId, readOnly = false, onClose }: PettyCashFormModalProps) {
  // savedEntryId is set after create — switches modal to upload mode without closing
  const [savedEntryId, setSavedEntryId] = useState<string | undefined>(entryId);
  const isEditing = !!entryId;
  const activeEntryId = savedEntryId;

  const queryClient = useQueryClient();
  const createPettyCash = useCreatePettyCash(claimId);
  const updatePettyCash = useUpdatePettyCash(claimId);
  const deleteReceipt = useDeleteReceipt(claimId);
  const { data: entriesData } = usePettyCashEntries(claimId);
  const { data: categoriesData } = useCategories();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const existingEntry = entriesData?.entries.find((e) => e.id === activeEntryId);

  const [form, setForm] = useState<CreatePettyCashInput & { categoryId: string }>({
    date: today(),
    description: '',
    amount: 0,
    categoryId: '',
  });
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const [previewReceipt, setPreviewReceipt] = useState<Receipt | null>(null);
  const [amountStr, setAmountStr] = useState('');

  // Pre-fill when editing
  useEffect(() => {
    if (existingEntry) {
      setForm({
        date: existingEntry.date.split('T')[0]!,
        description: existingEntry.description,
        amount: Number(existingEntry.amount),
        categoryId: existingEntry.categoryId,
      });
      setAmountStr(String(Number(existingEntry.amount)));
    }
  }, [existingEntry]);

  // Set default category when list loads
  useEffect(() => {
    if (categoriesData && categoriesData.categories.length > 0 && !activeEntryId) {
      setForm((f) => (
        f.categoryId
          ? f
          : { ...f, categoryId: categoriesData.categories[0]!.id }
      ));
    }
  }, [categoriesData, activeEntryId]);

  const validate = (): boolean => {
    const errs: typeof errors = {};
    if (!form.date) errs['date'] = 'Date is required';
    if (form.date && new Date(form.date) > new Date()) errs['date'] = 'Date cannot be in the future';
    if (!form.description?.trim()) errs['description'] = 'Description is required';
    if (!form.amount || form.amount <= 0) errs['amount'] = 'Amount must be greater than 0';
    if (!form.categoryId) errs['categoryId'] = 'Category is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (!validate()) return;
    setSubmitError(null);

    try {
      const payload: CreatePettyCashInput = {
        date: form.date,
        description: form.description.trim(),
        amount: form.amount,
        categoryId: form.categoryId,
      };

      if (isEditing) {
        await updatePettyCash.mutateAsync({ entryId: entryId!, data: payload });
        onClose();
      } else {
        const result = await createPettyCash.mutateAsync(payload);
        // Switch to upload mode — stay open so user can attach receipts immediately
        setSavedEntryId(result.entry.id);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setSubmitError(err.message);
      } else {
        setSubmitError('An error occurred. Please try again.');
      }
    }
  };

  // Receipt upload (Story 2.4, Multiple upload support with client-side auto-compression)
  const handleReceiptUpload = async (files: FileList | File[] | File) => {
    if (!activeEntryId) return;
    const fileArray = files instanceof FileList ? Array.from(files) : Array.isArray(files) ? files : [files];
    if (fileArray.length === 0) return;

    setReceiptError(null);
    setUploadingReceipt(true);
    try {
      // Compress photos on client side (e.g. 5-15MB -> ~400KB) using browser-image-compression
      const processedFiles = await Promise.all(
        fileArray.map(async (file) => {
          if (!file.type.startsWith('image/') || file.size < 350 * 1024) {
            return file;
          }
          try {
            const compressedBlob = await imageCompression(file, {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            });
            return new File([compressedBlob], file.name, {
              type: compressedBlob.type,
              lastModified: Date.now(),
            });
          } catch {
            return file;
          }
        })
      );

      const formData = new FormData();
      processedFiles.forEach((file) => {
        formData.append('receipt', file);
      });
      const response = await fetch(`/api/v1/claims/petty-cash/${activeEntryId}/receipts`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const body = (await response.json()) as { success: boolean; error?: { message: string } };
      if (!response.ok || !body.success) {
        throw new Error(body.error?.message ?? 'Upload failed');
      }
      // Refresh entry list so receipt thumbnails show immediately
      void queryClient.invalidateQueries({ queryKey: claimsKeys.pettyCash(claimId) });
      void queryClient.invalidateQueries({ queryKey: claimsKeys.expenses(claimId) });
    } catch (err) {
      setReceiptError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingReceipt(false);
    }
  };

  const isLoading = createPettyCash.isPending || updatePettyCash.isPending;

  return (
    <>
      <AnimatedModal onClose={onClose}>
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-[17px] font-semibold">
            {readOnly ? 'Petty Cash Entry Details' : isEditing ? 'Edit Petty Cash Entry' : savedEntryId ? 'Entry Saved — Add Receipts' : 'Add Petty Cash'}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex h-[30px] w-[30px] items-center justify-center rounded-md text-text-2 transition-colors hover:bg-surface-2 hover:text-text"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {submitError && (
            <div className="rounded-md border border-danger/20 bg-danger-soft px-4 py-3 text-sm text-danger">
              {submitError}
            </div>
          )}

          {/* Date */}
          <div>
            <label htmlFor="petty-cash-date" className="mb-1.5 block text-[13px] font-medium text-text">
              Date <span className="text-danger">*</span>
            </label>
            <input
              id="petty-cash-date"
              type="date"
              value={form.date}
              max={today()}
              disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
            {errors['date'] && <p className="mt-1 text-xs text-danger">{errors['date']}</p>}
          </div>

          {/* Category */}
          <div>
            <label htmlFor="petty-cash-category" className="mb-1.5 block text-[13px] font-medium text-text">
              Category <span className="text-danger">*</span>
            </label>
            <select
              id="petty-cash-category"
              value={form.categoryId}
              disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value }))}
              className="h-10 w-full rounded-md border border-input bg-surface px-3 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              {categoriesData?.categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            {errors['categoryId'] && <p className="mt-1 text-xs text-danger">{errors['categoryId']}</p>}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="petty-cash-description" className="mb-1.5 block text-[13px] font-medium text-text">
              Description <span className="text-danger">*</span>
            </label>
            <textarea
              id="petty-cash-description"
              rows={2}
              value={form.description}
              disabled={readOnly}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="e.g. Parking fee at Menara RHB"
              className="w-full rounded-md border border-input bg-surface px-3 py-2 text-sm text-text placeholder:text-text-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            />
            {errors['description'] && <p className="mt-1 text-xs text-danger">{errors['description']}</p>}
          </div>

          {/* Amount */}
          <div>
            <label htmlFor="petty-cash-amount" className="mb-1.5 block text-[13px] font-medium text-text">
              Amount (MYR) <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-2 font-semibold">
                RM
              </span>
              <input
                id="petty-cash-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amountStr}
                disabled={readOnly}
                onChange={(e) => {
                  setAmountStr(e.target.value);
                  setForm((f) => ({ ...f, amount: parseFloat(e.target.value) || 0 }));
                }}
                placeholder="0.00"
                className="h-10 w-full rounded-md border border-input bg-surface pl-10 pr-3 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              />
            </div>
            {errors['amount'] && <p className="mt-1 text-xs text-danger">{errors['amount']}</p>}
          </div>

          {/* Receipt Upload — shown after entry is saved (create or edit) */}
          {activeEntryId && (
            <div>
              <label className="mb-1.5 block text-[13px] font-semibold">Receipts</label>

              {existingEntry?.receipts && existingEntry.receipts.length > 0 && (
                <div className="mb-3 space-y-2">
                  <p className="text-xs font-semibold text-text flex items-center justify-between">
                    <span>Attached Receipts ({existingEntry.receipts.length})</span>
                  </p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {existingEntry.receipts.map((receipt) => {
                      const isPdf = receipt.mimeType === 'application/pdf' || receipt.originalFilename?.toLowerCase().endsWith('.pdf');
                      return (
                        <div key={receipt.id} className="flex items-center gap-2.5 rounded-lg border border-border bg-surface/60 px-3 py-2 text-xs transition-colors hover:border-border/80">
                          {isPdf ? (
                            <FileText className="h-4 w-4 shrink-0 text-red-500" strokeWidth={2} />
                          ) : (
                            <ReceiptText className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                          )}
                          <button
                            type="button"
                            onClick={() => setPreviewReceipt(receipt)}
                            className="flex-1 truncate text-left font-medium text-text hover:text-primary transition-colors underline-offset-2 hover:underline"
                          >
                            {receipt.originalFilename || 'Receipt'}
                          </button>
                          <span className="shrink-0 text-[11px] text-text-2">
                            {Math.round(receipt.size / 1024)} KB
                          </span>
                          {!readOnly && (
                            <button
                              type="button"
                              onClick={() => void deleteReceipt.mutateAsync(receipt.id)}
                              title="Remove receipt"
                              className="rounded p-1 text-text-2 transition-colors hover:bg-danger-soft hover:text-danger"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!readOnly && (
                <>
                  {receiptError && <p className="mb-2 text-xs text-danger">{receiptError}</p>}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingReceipt}
                      className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-input bg-surface-2/20 px-3 py-3 text-xs font-medium text-text-2 transition-colors hover:border-primary hover:bg-primary/5 hover:text-text disabled:opacity-50"
                    >
                      <Upload className="h-4 w-4 text-primary" strokeWidth={2} />
                      <span>{uploadingReceipt ? 'Uploading...' : 'Upload Receipts'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={uploadingReceipt}
                      className="flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-input bg-surface-2/20 px-3 py-3 text-xs font-medium text-text-2 transition-colors hover:border-primary hover:bg-primary/5 hover:text-text disabled:opacity-50"
                    >
                      <Camera className="h-4 w-4 text-primary" strokeWidth={2} />
                      <span>{uploadingReceipt ? 'Uploading...' : 'Take Photo'}</span>
                    </button>
                  </div>
                  <p className="mt-1.5 text-center text-[11px] text-text-2">
                    JPEG, PNG, WebP, PDF (Multiple files allowed, max 5MB each)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) void handleReceiptUpload(files);
                      e.target.value = '';
                    }}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      const files = e.target.files;
                      if (files && files.length > 0) void handleReceiptUpload(files);
                      e.target.value = '';
                    }}
                  />
                </>
              )}
            </div>
          )}

          {/* Submit */}
          <div className="mt-2 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-md border border-border px-[18px] text-sm font-medium transition-colors hover:bg-surface-2"
            >
              {readOnly ? 'Close' : savedEntryId && !isEditing ? 'Done' : 'Cancel'}
            </button>
            {!readOnly && (!savedEntryId || isEditing) && (
              <button
                id="petty-cash-submit-btn"
                type="submit"
                disabled={isLoading}
                className="h-10 rounded-md bg-primary px-[18px] text-sm font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Entry'}
              </button>
            )}
          </div>
          {!readOnly && (
            <p className="text-right text-xs text-text-2">
              This entry will be added to your draft claim.
            </p>
          )}
        </form>
      </AnimatedModal>
      {previewReceipt && (
        <ReceiptPreviewModal
          receipt={previewReceipt}
          onClose={() => setPreviewReceipt(null)}
        />
      )}
    </>
  );
}
